import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { band, members } from '@/constants/portfolio'
import { useFrame } from '@/hooks/use-frame'
import { audioEngine } from '@/lib/audio/engine'
import { documentTop, easeOutCubic, range } from '@/lib/motion'
import { useExperience } from '@/components/generals/experience-provider'
import InstrumentArt from '@/components/landing/instrument-art'

const GROUP_WIDTH: Record<string, string> = {
    'les-paul': 'w-[15%] tab:w-[9%]',
    'drum-kit': 'w-[38%] tab:w-[24%]',
    telecaster: 'w-[15%] tab:w-[9%]',
    bass: 'w-[13%] tab:w-[7.5%]'
}

const GROUP_ORDER = ['atta-rumnan', 'moses-dass', 'umar-naufal', 'arif-azmi']

const BookingPanel: FC = (): ReactNode => (
    <div className='plate relative p-6 xs:p-8' id='book'>
        <p className='mono-label text-[11px] text-ink-soft'>Booking and press</p>
        <h3 className='display-type mt-2 text-[clamp(2rem,3.6vw,3.2rem)]'>Book The Bluntsheep</h3>
        <p className='mt-4 max-w-[46ch] text-[17px] leading-[1.6]'>
            For gigs, campus shows, festivals and press, send the band a direct message on Instagram. Line-up, songs and story are all on this page for your shortlist.
        </p>

        <div className='mt-6 flex flex-col gap-3 xs:flex-row xs:flex-wrap'>
            <a
                href={band.socialLinks.instagram}
                target='_blank'
                rel='noopener noreferrer'
                onClick={() => audioEngine.cue('confirm')}
                className='group relative inline-flex min-h-14 items-center justify-center gap-3 overflow-hidden bg-bandana px-6 text-paper'
            >
                <span aria-hidden='true' className='absolute -inset-y-1 -left-4 w-[calc(100%+32px)] -translate-x-[110%] -skew-x-12 bg-ink transition-transform duration-300 ease-out group-hover:translate-x-0 group-focus-visible:translate-x-0' />
                <span className='relative text-[16px] font-bold'>Book via Instagram DM</span>
                <span aria-hidden='true' className='relative block h-0 w-0 border-y-[6px] border-l-[10px] border-y-transparent border-l-current' />
                <span className='sr-only'> (opens in a new tab)</span>
            </a>

            {band.socialLinks.pressKitUrl && (
                <a
                    href={band.socialLinks.pressKitUrl}
                    download
                    className='inline-flex min-h-14 items-center justify-center border-2 border-ink px-6 text-[16px] font-bold transition-colors hover:bg-ink hover:text-paper'
                >
                    Download press kit (PDF)
                </a>
            )}
        </div>

        <p className='mono-label mt-5 text-[11px] text-ink-soft'>
            Instagram{' '}
            <a href={band.socialLinks.instagram} target='_blank' rel='noopener noreferrer' className='text-ink underline underline-offset-4'>
                {band.socialLinks.instagramHandle}
            </a>
        </p>
    </div>
)

// The vision: a statement that fills as it scrolls in, the booking panel,
// and the four instruments rising from the bottom edge like the band walking off.
const VisionSection: FC = (): ReactNode => {
    const { immersive } = useExperience()
    const sectionRef = useRef<HTMLElement>(null)
    const fillRef = useRef<HTMLSpanElement>(null)
    const groupRef = useRef<HTMLDivElement>(null)
    const measure = useRef({ at: -1, top: 0, height: 1 })

    useFrame(state => {
        const section = sectionRef.current
        if (!section) return

        const vh = state.viewportH

        if (state.time - measure.current.at > 0.5) {
            measure.current = { at: state.time, top: documentTop(section), height: section.offsetHeight }
        }

        const { top, height } = measure.current
        const y = state.scrollY
        if (y + vh < top - 200 || y > top + height + 200) return

        const fill = easeOutCubic(range(y, top - vh * 0.75, top - vh * 0.1))
        if (fillRef.current) fillRef.current.style.clipPath = `inset(-10% ${(100 - fill * 100).toFixed(2)}% -10% 0)`

        const rise = easeOutCubic(range(y + vh, top + height * 0.62, top + height))
        if (groupRef.current) groupRef.current.style.transform = `translate3d(0, ${((1 - rise) * 55).toFixed(2)}%, 0)`
    }, immersive)

    const ordered = GROUP_ORDER
        .map(id => members.find(member => member.id === id))
        .filter((member): member is NonNullable<typeof member> => Boolean(member))

    return (
        <section ref={sectionRef} id='vision' aria-labelledby='vision-title' className='relative overflow-hidden bg-paper'>
            <div className='relative mx-auto max-w-[1680px] px-5 pt-24 xs:px-8 tab:px-12 tab:pt-32'>
                <p className='mono-label text-[11px] text-ink-soft'>05 / Vision</p>

                <h2 id='vision-title' className='display-type relative mt-4 text-[clamp(3.2rem,10.4vw,11.5rem)] leading-[0.84]'>
                    <span className={cn(immersive ? 'text-ink/[0.13]' : 'text-ink')}>{band.visionStatement}</span>
                    {immersive && (
                        <span
                            ref={fillRef}
                            aria-hidden='true'
                            className='absolute inset-0 text-ink'
                            style={{ clipPath: 'inset(-10% 100% -10% 0)' }}
                        >
                            {band.visionStatement}
                        </span>
                    )}
                </h2>

                <div className='mt-12 grid gap-10 tab:mt-16 tab:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] tab:gap-16'>
                    <div>
                        <p className='text-[clamp(1.2rem,1.8vw,1.6rem)] font-medium leading-[1.45]'>{band.visionText}</p>

                        <ol className='mt-8 grid gap-3 xs:grid-cols-3' aria-label='Where the band is headed'>
                            {['Malaysia', 'The region', 'The world'].map((stop, index) => (
                                <li key={stop} className='flex items-center gap-3 border-t-2 border-ink pt-3'>
                                    <span className='mono-label text-[10px] text-ink-soft'>{String(index + 1).padStart(2, '0')}</span>
                                    <span className='display-type text-[1.35rem]'>{stop}</span>
                                </li>
                            ))}
                        </ol>
                    </div>

                    <BookingPanel />
                </div>
            </div>

            <div className='relative mt-20 h-[clamp(220px,34vw,460px)] overflow-hidden tab:mt-24'>
                <div aria-hidden='true' className='absolute inset-x-0 bottom-0 h-[38%] bg-ink' />

                <div
                    ref={groupRef}
                    className='absolute inset-x-0 bottom-[-6%] flex items-end justify-center gap-[3vw] will-change-transform'
                    style={immersive ? { transform: 'translate3d(0, 55%, 0)' } : undefined}
                >
                    {ordered.map((member, index) => (
                        <figure key={member.id} className={cn('relative m-0 flex flex-col items-center', GROUP_WIDTH[member.instrument])}>
                            <div style={{ transform: `rotate(${[-6, 0, 5, -4][index]}deg)` }} className='w-full'>
                                <InstrumentArt kind={member.instrument} />
                            </div>
                            <figcaption className='mono-label absolute bottom-[42%] left-1/2 -translate-x-1/2 whitespace-nowrap border-2 border-ink bg-paper-hi px-2 py-1 text-[10px] paper-shadow-sm'>
                                {member.shortName}
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </div>
        </section>
    )
}

export default VisionSection
